/**
 * Shared module data fetching hook.
 *
 * Provides standardized data fetching pattern for exam modules.
 * Eliminates duplication between reading and listening module hooks.
 *
 * @module useModuleData
 */

import { useQuery } from '@tanstack/react-query';
import { STALE_TIME_FIVE_MINUTES } from '@/lib/constants';
import type {
  ExamModule,
  ExamType,
  CEFRLevel,
  SimulationSettings,
  SimulationData,
} from '@/types/exam-simulation';

/**
 * Options for module data fetching hook
 */
interface UseModuleDataOptions {
  /** Exam module type */
  module: ExamModule;
  /** Exam type (goethe, osd, or telc) */
  examType: ExamType;
  /** CEFR proficiency level */
  level: CEFRLevel;
  /** Simulation settings from API */
  settings: SimulationSettings | null;
  /** Current user ID (optional) */
  userId?: string | null;
  /**
   * Ordered question UUIDs to pin (resume flow). When provided, the fetch
   * requests exactly this set instead of a fresh randomized selection. Omit
   * (or pass empty) on fresh runs to keep the adaptive selection unchanged.
   */
  pinnedQuestionIds?: string[];
  /** Function to fetch module data */
  fetchFn: (
    level: CEFRLevel,
    examType: ExamType,
    sectionSettings: Record<string, unknown>,
    userId?: string | null,
    pinnedQuestionIds?: string[] | null,
  ) => Promise<SimulationData>;
}

/**
 * Return type for module data hook
 */
interface UseModuleDataReturn {
  /** Fetched simulation data */
  data: SimulationData | null;
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Error object if fetch failed */
  error: Error | null;
}

/**
 * Hook for fetching exam module data with standardized pattern.
 *
 * Provides consistent React Query setup for all exam modules.
 * Handles loading states, error states, and caching automatically.
 *
 * @param options - Data fetching configuration
 * @returns Object containing data, loading state, and error
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useModuleData({
 *   module: 'reading',
 *   examType: 'goethe',
 *   level: 'B1',
 *   settings: simulationSettings,
 *   userId: user?.id,
 *   fetchFn: fetchReadingModuleData,
 * });
 * ```
 */
export function useModuleData({
  module,
  examType,
  level,
  settings,
  userId,
  pinnedQuestionIds,
  fetchFn,
}: UseModuleDataOptions): UseModuleDataReturn {
  const hasPins = !!pinnedQuestionIds && pinnedQuestionIds.length > 0;

  // Keep the unpinned key identical to the media preloader's so fresh runs
  // hit the prefetched cache. Resume (pinned) uses a distinct key, forcing a
  // fetch for the exact saved set rather than reusing a random preload.
  const queryKey = hasPins
    ? [`${module}ModuleData`, examType, level, 'pinned', pinnedQuestionIds]
    : [`${module}ModuleData`, examType, level];

  const {
    data: simulationData,
    isLoading,
    error: dataError,
  } = useQuery<SimulationData | null>({
    queryKey,
    queryFn: () => {
      if (!settings) {
        return Promise.resolve(null);
      }
      return fetchFn(
        level,
        examType,
        settings.sectionSettings,
        userId,
        hasPins ? pinnedQuestionIds : null,
      );
    },
    enabled: !!settings && !!examType && !!level,
    staleTime: STALE_TIME_FIVE_MINUTES,
    // An in-progress exam must keep the exact question set it started with.
    // A background refetch would hand back a freshly shuffled SimulationData
    // object whose new reference flows into the audio playback effects'
    // dependencies, tearing down (audio.pause + src='') any section/context
    // clip mid-play. Disable the disruptive auto-refetches for the duration
    // of the module; the initial fetch, preloader seeding, and resume path
    // are unaffected.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: simulationData ?? null,
    isLoading,
    error: dataError ?? null,
  };
}

